import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, ArrowUpDown, SlidersHorizontal, Table, KanbanSquare } from "lucide-react";
import { CandidateAvatar } from "../CandidateComponents";
import { intentBadge, qualifiedBadge, channelBadge, StatusChip, LinkedInLink } from "./responseBadges";
import Pagination from "../../../components/shared_components/Pagination";
import useServerPagination from "../../hooks/useServerPagination";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const INTENT_TABS = [
    { key: "", label: "All" },
    { key: "interested", label: "Interested" },
    { key: "not_interested", label: "Not interested" },
    { key: "unknown", label: "Awaiting response" },
];

// Board columns, grouped by response intent.
const INTENT_COLUMNS = [
    { key: "interested",     label: "Interested",     color: "#34d399" },
    { key: "unknown",        label: "Awaiting",       color: "#9ca3af" },
    { key: "not_interested", label: "Not interested", color: "#f87171" },
];
const intentKey = (intent) => (intent === "interested" || intent === "not_interested" ? intent : "unknown");

const ScreeningResponses = () => {
    const navigate = useNavigate();
    const orgId = localStorage.getItem("current_org_id");

    const [intent, setIntent] = useState("");
    const [maxNotice, setMaxNotice] = useState("");
    const [location, setLocation] = useState("");
    const [qualifiedOnly, setQualifiedOnly] = useState(false);
    const [search, setSearch] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [sortAsc, setSortAsc] = useState(true);
    const [viewMode, setViewMode] = useState("table");   // "table" | "board"
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(new Set());

    // Search runs server-side now — debounce so typing doesn't fire a request per key.
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Pagination applies to the table only — the board shows each intent column in
    // full, so it asks for the whole set (the API returns everything without `limit`).
    const {
        currentPage,
        itemsPerPage,
        handlePageChange,
        handleItemsPerPageChange,
    } = useServerPagination({
        totalItems: total,
        resetKey: `${intent}|${debouncedSearch}|${sortAsc}|${viewMode}`,
    });

    const fetchResponses = useCallback(() => {
        setLoading(true);
        setError(null);
        const params = { org_id: orgId, sort_by: "full_name", sort_dir: sortAsc ? "asc" : "desc" };
        if (intent) params.intent = intent;
        if (maxNotice) params.max_notice_period_days = maxNotice;
        if (location.trim()) params.location = location.trim();
        if (qualifiedOnly) params.qualified = true;
        if (debouncedSearch) params.search = debouncedSearch;
        if (viewMode === "table") {
            params.page = currentPage;
            params.limit = itemsPerPage;
        }
        axios
            .get(`${SOURCING_URL}/engagement/responses`, { params })
            .then(({ data }) => {
                setRows(data.engagements || []);
                setTotal(data.total ?? (data.engagements || []).length);
            })
            .catch(() => setError("Failed to load responses."))
            .finally(() => setLoading(false));
    }, [orgId, intent, maxNotice, location, qualifiedOnly, debouncedSearch, sortAsc, viewMode, currentPage, itemsPerPage]);

    // Refetch on tab / search / sort / page changes. The drawer filters (notice, location,
    // qualified) still apply via the Apply button, which calls fetchResponses directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchResponses(); }, [intent, debouncedSearch, sortAsc, viewMode, currentPage, itemsPerPage]);

    // The board groups the full set it was given; the table renders the server's page.
    const visible = rows;

    // Selection is page-scoped — only the current page is in memory.
    const allOnPageSelected = rows.length > 0 && rows.every(r => selected.has(r.engagement_id));
    const togglePage = () =>
        setSelected(prev => {
            const next = new Set(prev);
            rows.forEach(r =>
                allOnPageSelected ? next.delete(r.engagement_id) : next.add(r.engagement_id)
            );
            return next;
        });
    const toggleOne = (id) =>
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const th = "px-4 py-3 text-left text-[13px] font-semibold text-gray-600 whitespace-nowrap";
    const td = "px-4 py-4 text-sm text-gray-600 align-middle";

    const boardView = (
        <div className="flex gap-4 overflow-x-auto py-4">
            {INTENT_COLUMNS.map(col => {
                const colRows = visible.filter(r => intentKey(r.intent) === col.key);
                return (
                    <div key={col.key} className="flex-shrink-0 w-80 rounded-2xl bg-gray-50 dark:bg-white/[0.03] p-3">
                        <div className="flex items-center gap-2 px-1 pb-3">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{col.label}</h3>
                            <span className="text-xs text-gray-400 dark:text-gray-500">{colRows.length}</span>
                        </div>

                        <div className="flex flex-col gap-2.5 min-h-[80px]">
                            {colRows.map(r => {
                                const c = r.candidate || {};
                                return (
                                    <div key={r.engagement_id} className="bg-white dark:bg-white/5 rounded-xl border border-gray-100 dark:border-gray-700/50 p-3 hover:shadow-sm hover:border-purple-100 transition-all">
                                        <div className="flex items-start gap-2.5">
                                            <CandidateAvatar candidate={c} size={32} />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5">
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{c.full_name || "Unknown"}</p>
                                                    <LinkedInLink url={c.linkedin_url} />
                                                </div>
                                                {c.current_title && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                        {c.current_title}{c.current_company ? ` · ${c.current_company}` : ""}
                                                    </p>
                                                )}
                                                {c.email && <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{c.email}</p>}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 mt-2.5 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
                                            <StatusChip badge={qualifiedBadge(r.is_qualified)} />
                                            <StatusChip badge={channelBadge(r.channel)} />
                                            {r.notice_period_days != null && (
                                                <span>Notice: <span className="text-gray-700 dark:text-gray-300">{r.notice_period_days}d</span></span>
                                            )}
                                            {r.expected_ctc && (
                                                <span>CTC: <span className="text-gray-700 dark:text-gray-300">{r.expected_ctc}</span></span>
                                            )}
                                            {r.submitted_at && (
                                                <span className="ml-auto">{new Date(r.submitted_at).toLocaleDateString()}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {colRows.length === 0 && (
                                <div className="text-center text-xs text-gray-300 dark:text-gray-600 py-6">No responses</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-white/[0.03]">
            {/* Page header */}
            <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-base font-semibold text-gray-800 dark:text-white">Screening Responses</h1>
            </div>

            {/* Toolbar */}
            <div className="bg-white dark:bg-white/5 px-6 py-3 flex items-center justify-between gap-4 flex-wrap border-b border-gray-100 dark:border-gray-700/50">
                <div className="flex items-center gap-2 flex-wrap">
                    {INTENT_TABS.map(t => {
                        const active = intent === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setIntent(t.key)}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                                    active
                                        ? "text-white"
                                        : "border border-gray-200 text-gray-600 hover:border-gray-300"
                                }`}
                                style={active ? { background: "linear-gradient(to right, #ec4899, #3b82f6)" } : undefined}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search by name"
                            name="responses-search"
                            autoComplete="off"
                            className="w-64 text-sm border border-gray-200 dark:border-gray-600 rounded-full pl-9 pr-3 py-2 outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-100"
                        />
                    </div>
                    <button
                        onClick={() => setShowFilters(s => !s)}
                        title="Filters"
                        className={`p-2 rounded-full border transition-all ${showFilters ? "border-purple-400 text-purple-600 bg-purple-50" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                    >
                        <SlidersHorizontal className="w-4 h-4" />
                    </button>

                    {/* Table / Board toggle */}
                    <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-600 rounded-lg p-0.5">
                        <button
                            onClick={() => setViewMode("table")}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "table" ? "bg-purple-100 text-purple-700" : "text-gray-400 hover:text-gray-600"}`}
                        >
                            <Table className="w-4 h-4" /> Table
                        </button>
                        <button
                            onClick={() => setViewMode("board")}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "board" ? "bg-purple-100 text-purple-700" : "text-gray-400 hover:text-gray-600"}`}
                        >
                            <KanbanSquare className="w-4 h-4" /> Board
                        </button>
                    </div>
                </div>
            </div>

            {/* Secondary filters */}
            {showFilters && (
                <div className="bg-white dark:bg-white/5 px-6 py-3 border-b border-gray-100 dark:border-gray-700/50 flex items-end gap-3 flex-wrap">
                    <div>
                        <label className="text-[11px] text-gray-500 dark:text-gray-400 block mb-1">Max notice (days)</label>
                        <input
                            type="number"
                            value={maxNotice}
                            onChange={e => setMaxNotice(e.target.value)}
                            className="w-32 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 outline-none focus:border-purple-400"
                            placeholder="e.g. 45"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] text-gray-500 dark:text-gray-400 block mb-1">Location</label>
                        <input
                            type="text"
                            name="location-filter"
                            autoComplete="off"
                            value={location}
                            onChange={e => setLocation(e.target.value)}
                            className="w-44 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 outline-none focus:border-purple-400"
                            placeholder="e.g. Pune"
                        />
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer pb-2">
                        <input
                            type="checkbox"
                            checked={qualifiedOnly}
                            onChange={e => setQualifiedOnly(e.target.checked)}
                            className="accent-purple-600"
                        />
                        Qualified only
                    </label>
                    <button
                        onClick={fetchResponses}
                        className="px-4 py-1.5 rounded-lg text-sm font-medium text-white"
                        style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                    >
                        Apply
                    </button>
                </div>
            )}

            {/* Table */}
            <div className="px-2 sm:px-4">
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-7 h-7 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <p className="text-sm text-red-500 dark:text-red-400 text-center py-20">{error}</p>
                ) : total === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">No responses match these filters</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Try a different tab or widen your filters.</p>
                    </div>
                ) : viewMode === "board" ? (
                    boardView
                ) : (
                    <>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[860px] border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-200 dark:border-gray-600">
                                    <th className="px-4 py-3 w-10">
                                        <input
                                            type="checkbox"
                                            checked={allOnPageSelected}
                                            ref={el => {
                                                if (el) el.indeterminate =
                                                    !allOnPageSelected && rows.some(r => selected.has(r.engagement_id));
                                            }}
                                            onChange={togglePage}
                                            className="accent-purple-600 cursor-pointer"
                                        />
                                    </th>
                                    <th className={th}>
                                        <button onClick={() => setSortAsc(s => !s)} className="inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-white">
                                            Candidate Name <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                        </button>
                                    </th>
                                    <th className={th}>Job Role</th>
                                    <th className={th}>Email ID</th>
                                    <th className={th}>Qualified</th>
                                    <th className={th}>Notice Period</th>
                                    <th className={th}>Expected CTC</th>
                                    <th className={th}>Status</th>
                                    <th className={th}>Responded</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => {
                                    const c = r.candidate || {};
                                    const badge = intentBadge(r.intent);
                                    const score = qualifiedBadge(r.is_qualified);
                                    return (
                                        <tr key={r.engagement_id} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                            <td className="px-4 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(r.engagement_id)}
                                                    onChange={() => toggleOne(r.engagement_id)}
                                                    className="accent-purple-600 cursor-pointer"
                                                />
                                            </td>
                                            <td className={td}>
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <CandidateAvatar candidate={c} size={36} />
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="font-semibold text-gray-900 dark:text-white truncate">{c.full_name || "Unknown"}</span>
                                                            <LinkedInLink url={c.linkedin_url} />
                                                        </div>
                                                        {c.location && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{c.location}</p>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={td}>
                                                <span className="text-gray-700 dark:text-gray-300">{c.current_title || "-"}</span>
                                                {c.current_company && <span className="text-gray-400 dark:text-gray-500"> · {c.current_company}</span>}
                                            </td>
                                            <td className={td}>{c.email || <span className="text-gray-300 dark:text-gray-600">-</span>}</td>
                                            <td className={td}>
                                                {score ? <StatusChip badge={score} /> : <span className="text-gray-300 dark:text-gray-600">-</span>}
                                            </td>
                                            <td className={td}>{r.notice_period_days != null ? `${r.notice_period_days} days` : <span className="text-gray-300 dark:text-gray-600">-</span>}</td>
                                            <td className={td}>{r.expected_ctc || <span className="text-gray-300 dark:text-gray-600">-</span>}</td>
                                            <td className={td}>
                                                <div className="flex items-center gap-1.5">
                                                    <StatusChip badge={badge} />
                                                    <StatusChip badge={channelBadge(r.channel)} />
                                                </div>
                                            </td>
                                            <td className={td}>
                                                {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : <span className="text-gray-300 dark:text-gray-600">-</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="py-4">
                        <Pagination
                            currentPage={currentPage}
                            totalItems={total}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            label="Responses"
                        />
                    </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ScreeningResponses;
